package com.havn.app.core.media

import androidx.compose.foundation.Image
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.toComposeImageBitmap
import androidx.compose.ui.layout.ContentScale
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.fetch.RequestInit
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.jetbrains.skia.Image.Companion.makeFromEncoded
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine

@OptIn(ExperimentalEncodingApi::class)
@Composable
actual fun HavnDatabaseImage(
    dataUrl: String?,
    contentDescription: String?,
    modifier: Modifier,
    contentScale: ContentScale
): Boolean {
    var remoteImageBitmap by remember(dataUrl) { mutableStateOf<ImageBitmap?>(dataUrl?.let { HavnDatabaseImageMemoryCache[it] }) }
    LaunchedEffect(dataUrl) {
        remoteImageBitmap = dataUrl?.let { HavnDatabaseImageMemoryCache[it] }
        if (dataUrl?.startsWith("http") == true) {
            remoteImageBitmap = remoteImageBitmap ?: runCatching {
                val response = window.fetch(dataUrl, RequestInit()).await()
                val arrayBuffer = response.arrayBuffer().await()
                makeFromEncoded(Uint8Array(arrayBuffer).toByteArray()).toComposeImageBitmap()
            }.getOrNull()
                ?: decodeRemoteImageWithBrowser(dataUrl)
            remoteImageBitmap?.also { decoded -> HavnDatabaseImageMemoryCache[dataUrl] = decoded }
        }
    }

    val imageBitmap = remember(dataUrl) {
        dataUrl?.takeUnless { it.startsWith("http") }?.let { HavnDatabaseImageMemoryCache[it] }
            ?: dataUrl
                ?.takeUnless { it.startsWith("http") }
                ?.decodeDatabaseImage()
                ?.also { decoded -> HavnDatabaseImageMemoryCache[dataUrl] = decoded }
    }

    val resolvedImageBitmap = remoteImageBitmap ?: imageBitmap
    return if (resolvedImageBitmap == null) {
        false
    } else {
        Image(
            bitmap = resolvedImageBitmap,
            contentDescription = contentDescription,
            modifier = modifier,
            contentScale = contentScale
        )
        true
    }
}

private fun Uint8Array.toByteArray(): ByteArray =
    ByteArray(length) { index -> (this.asDynamic()[index] as Number).toInt().toByte() }

private suspend fun decodeRemoteImageWithBrowser(url: String): ImageBitmap? = suspendCoroutine { continuation ->
    val image = document.createElement("img") as HTMLImageElement
    var completed = false

    fun finish(bitmap: ImageBitmap?) {
        if (!completed) {
            completed = true
            continuation.resume(bitmap)
        }
    }

    image.asDynamic().crossOrigin = "anonymous"
    image.onload = {
        val bitmap = runCatching {
            val width = image.naturalWidth.takeIf { it > 0 } ?: image.width
            val height = image.naturalHeight.takeIf { it > 0 } ?: image.height
            if (width <= 0 || height <= 0) {
                null
            } else {
                val canvas = document.createElement("canvas") as HTMLCanvasElement
                canvas.width = width
                canvas.height = height
                canvas.getContext("2d")
                    ?.asDynamic()
                    ?.drawImage(image, 0, 0, width, height)
                canvas.toDataURL("image/png").decodeDatabaseImage()
            }
        }.getOrNull()
        finish(bitmap)
        Unit
    }
    image.onerror = { _, _, _, _, _ ->
        finish(null)
        Unit
    }
    image.src = url
}

@OptIn(ExperimentalEncodingApi::class)
private fun String.decodeDatabaseImage(): ImageBitmap? =
    substringAfter("base64,", missingDelimiterValue = "")
        .takeIf { it.isNotBlank() }
        ?.let { encoded ->
            runCatching {
                makeFromEncoded(Base64.decode(encoded)).toComposeImageBitmap()
            }.getOrNull()
        }

private object HavnDatabaseImageMemoryCache {
    private const val MaxEntries = 128
    private val values = mutableMapOf<String, ImageBitmap>()
    private val order = ArrayDeque<String>()

    operator fun get(source: String): ImageBitmap? = values[cacheKey(source)]

    operator fun set(source: String, bitmap: ImageBitmap) {
        val key = cacheKey(source)
        if (values.containsKey(key)) {
            order.remove(key)
        }
        values[key] = bitmap
        order.addLast(key)
        while (order.size > MaxEntries) {
            values.remove(order.removeFirst())
        }
    }

    private fun cacheKey(source: String): String =
        if (source.startsWith("http")) source else "${source.length}:${source.hashCode()}"
}
